import { type FC, useCallback, useMemo, useState } from "react";
import OrderDetailsAddToCartFlow from "~/features/orders/orderDetails/components/AddToCart/OrderDetailsAddToCartFlow";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import {
  getOrderLineItemSurchargeValue,
  getOrderLineItemTotalPrice,
} from "~/features/orders/common/utils/OrderLineItemUtils";
import { sum } from "~/utils/PriceUtils";
import { type DetailedOrderData, type DetailedOrderLineItemData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";
import { type CaseDataObject } from "~/features/orders/orderDetails/components/ServiceCases/types";
import { ATCBar } from "~/features/orders/common/components/ATCBar";
import OrderTableV2 from "./OrderTableV2";
import { Box, Stack, Typography } from "~/common/mui";

interface OrderDetailsProps {
  data: DetailedOrderData;
  serviceCases?: Record<string, CaseDataObject[]>;
}

interface selectedATCItemsProps {
  selectedLineItems: number;
  selectedQuantity: number;
  totalPrice: PriceData;
}

type Accumulator = {
  totalPieces: number;
  totalPrice: number;
  currencyIso: string;
};

const selectedATCItemsDefaultValue = {
  selectedLineItems: 0,
  selectedQuantity: 0,
  totalPrice: { value: 0, currencyIso: "" },
};

const OrderDetailsV2: FC<OrderDetailsProps> = ({ data, serviceCases }) => {
  const { t } = useTranslation();

  const canAddToCart = userHasPermission(Permission.ADD_TO_CART);
  const canAddToWishlist = userHasPermission(Permission.ADD_TO_WISHLIST);
  const showCheckboxes = canAddToCart || canAddToWishlist;
  const [selected, setSelected] = useState<number[]>([]);
  const selectedItems = selected.map((index) => data.products[index]);

  const { val: showAddToCart, setTrue: openAddToCart, setFalse: closeAddToCart } = useBooleanState(false);

  const selectedATCItems = useMemo(() => getSelectedATCItems(selectedItems), [selectedItems]);

  const selectItem = useCallback((itemIndex: number) => {
    setSelected((selected) => {
      if (selected.indexOf(itemIndex) === -1) {
        return selected.concat(itemIndex);
      } else {
        return selected.filter((item) => item !== itemIndex);
      }
    });
  }, []);

  const totalSurcharge = useMemo(() => {
    return sum(...data.products.map((lineItem) => getOrderLineItemSurchargeValue(lineItem)));
  }, [data]);

  const totalProducts = useMemo(() => {
    let sum = 0;
    data.products.map((product) => {
      if (product.subEntries && product.subEntries?.length > 0) {
        sum += product.subEntries?.length ?? 0;
      } else {
        sum += 1;
      }
    });
    return sum;
  }, [data]);

  const constainsSurcharge = totalSurcharge.value > 0;

  return (
    <>
      <Stack gap={2}>
        <Typography variant="titleMedium" component={"b"}>
          {t("orderDetails.header.toBeDelivered", { count: totalProducts })}
        </Typography>

        <Stack gap={2}>
          <OrderTableV2
            selected={selected}
            selectItem={selectItem}
            selectable={showCheckboxes}
            orderDetailsData={data}
            serviceCaseData={serviceCases}
          />
        </Stack>
      </Stack>

      {
        <Box mt={3}>
          <IncludesSurchargeDisclaimer hasSurcharge={constainsSurcharge} />
        </Box>
      }

      {showAddToCart && <OrderDetailsAddToCartFlow onClose={closeAddToCart} selectedOrderItems={selectedItems} />}

      {selectedATCItems.selectedLineItems > 0 && (
        <ATCBar
          selectedLineItems={selectedATCItems.selectedLineItems}
          selectedQuantity={selectedATCItems.selectedQuantity}
          totalPrice={selectedATCItems.totalPrice}
          onAddToCart={openAddToCart}
        />
      )}
    </>
  );
};

export default OrderDetailsV2;

const getSelectedATCItems = (selectedItems: DetailedOrderLineItemData[]): selectedATCItemsProps => {
  if (selectedItems.length === 0) {
    return selectedATCItemsDefaultValue;
  }

  const currencyIso = getOrderLineItemTotalPrice(selectedItems[0]).currencyIso;

  const { totalPieces, totalPrice } = selectedItems.reduce<Accumulator>(
    (acc, item) => {
      acc.totalPieces += item.confirmedQty;

      const priceData = getOrderLineItemTotalPrice(item);
      if (priceData.currencyIso !== currencyIso) {
        throw new Error("Mismatched currencies in selected items");
      }
      acc.totalPrice += priceData.value;

      return acc;
    },
    { totalPieces: 0, totalPrice: 0, currencyIso: "" },
  );

  return {
    selectedLineItems: selectedItems.length,
    selectedQuantity: totalPieces,
    totalPrice: {
      currencyIso,
      value: totalPrice,
    },
  };
};
